/**
 * DqxTable -- Virtualized data quality assessment table.
 *
 * Uses a single CSS grid-template-columns definition shared by the sticky
 * header and every virtualised data row, guaranteeing pixel-perfect column
 * alignment regardless of content width.
 *
 * Features:
 *  - Dual-row sticky header: source column name (light) + mapped target name (bold)
 *  - Row virtualization via @tanstack/react-virtual for 1000s of rows
 *  - Horizontal scrolling for wide files
 *  - Error cells: red (single-column) or amber (multi-column / cross-field); hover tooltip for rules
 *  - Row numbers column
 *  - Manual Cleanse inline edit: free-text or reference-list dropdown
 */

import { useRef, useState, useCallback, useMemo } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Pencil, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTelemetry } from "@/hooks/useTelemetry";
import type { CellEdit, DqxColumnMapping, DqxValidationResult } from "@/types";
import { normalizeDqxRuleName, type DqxContractRuleMeta } from "@/services/dqxContractRulesLookup";
import {
  mapContractRuleTypeToBucket,
  type DqxSummaryCheckTypeBucket,
} from "@/services/dqxSummaryMetrics";

// ── Constants ─────────────────────────────────────────────────

const ROW_HEIGHT = 38;
const HEADER_HEIGHT = 52;
const OVERSCAN = 12;
const ROW_NUM_WIDTH = 52;
const COL_WIDTH = 170;

// Stable identity so consumers that don't pass cellEdits don't retrigger memos.
const EMPTY_EDITS: Map<string, CellEdit> = new Map();

// ── Types ─────────────────────────────────────────────────────

interface DqxTableProps {
  headers: string[];
  rows: Record<string, unknown>[];
  columnMappings: DqxColumnMapping[];
  validation: DqxValidationResult;
  /** When set, only these original row indices are shown; cell error keys still use the same indices. */
  visibleRowIndices?: number[];
  /** When set, only highlight cells whose rule(s) map to these buckets. */
  selectedBuckets?: DqxSummaryCheckTypeBucket[];
  /** Normalized rule name -> contract metadata (rule_type for bucket filter). */
  contractRuleLookup?: Map<string, DqxContractRuleMeta> | null;
  /** Source column name -> allowed values for constrained dropdown editing. */
  columnAllowedValuesLookup?: Map<string, string[]> | null;
  maxHeight?: number | string;
  className?: string;
  /** When false, error cells stay red but clicks are inert. */
  canEdit?: boolean;
  /** Map of pending/revalidated edits keyed by "rowIndex-colIndex". */
  cellEdits?: Map<string, CellEdit>;
  /** Parent enriches the payload with userId/userEmail/timestamp/status. */
  onCellEdited?: (edit: Omit<CellEdit, "userId" | "userEmail" | "timestamp" | "status">) => void;
  /** Fires when the user commits an empty value over a previously edited cell. */
  onCellReverted?: (key: string) => void;
}

// ── Error Tooltip ─────────────────────────────────────────────

function ErrorTooltip({
  rules,
  visible,
  x,
  y,
}: {
  rules: string[];
  visible: boolean;
  x: number;
  y: number;
}) {
  if (!visible || rules.length === 0) return null;

  return (
    <div
      className="fixed z-[60] max-w-xs px-3 py-2.5 rounded-lg bg-[var(--color-nav-bg)] text-white text-[11px] leading-relaxed shadow-xl pointer-events-none"
      style={{ left: x, top: y, transform: "translate(-50%, -100%) translateY(-8px)" }}
    >
      <p className="font-semibold text-red-300 mb-1">
        Failed {rules.length} rule{rules.length > 1 ? "s" : ""}:
      </p>
      <ul className="space-y-0.5">
        {rules.map((rule, i) => (
          <li key={i} className="flex gap-1.5">
            <span className="text-red-400 shrink-0">*</span>
            <span>{rule}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Constrained Dropdown Editor ─────────────────────────────────────────────

function ConstrainedDropdownEditor({
  originalValue,
  allowedValues,
  prioritizedValues = [],
  search,
  activeIndex,
  onSearchChange,
  onActiveIndexChange,
  onCommit,
  onCancel,
}: {
  originalValue: string;
  allowedValues: string[];
  prioritizedValues: string[];
  search: string;
  activeIndex: number;
  onSearchChange: (value: string) => void;
  onActiveIndexChange: (index: number) => void;
  onCommit: (value: string) => void;
  onCancel: () => void;
}) {
  const filteredValues = useMemo(() => {
    const q = search.trim().toLowerCase();

    const filtered = q ? allowedValues.filter((v) => v.toLowerCase().includes(q)) : allowedValues;

    const pinned = prioritizedValues.filter((v) =>
      filtered.some((item) => item.toLowerCase() === v.toLowerCase())
    );

    const remaining = filtered.filter(
      (v) => !pinned.some((p) => p.toLowerCase() === v.toLowerCase())
    );

    return [...pinned, ...remaining];
  }, [allowedValues, prioritizedValues, search]);

  const isPrioritizedValue = useCallback(
    (value: string) => prioritizedValues.some((v) => v.toLowerCase() === value.toLowerCase()),
    [prioritizedValues]
  );

  return (
    <div
      className="absolute left-0 top-full z-20 w-full min-w-[220px] rounded-md border border-amber-300 bg-white shadow-lg"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="border-b border-[var(--color-border-light)] px-2 py-2">
        <p className="mb-1 text-[10px] text-[var(--color-text-tertiary)]">Original invalid value</p>
        <p className="truncate text-xs text-gray-400">{originalValue || "Empty"}</p>
      </div>

      <div className="border-b border-[var(--color-border-light)] p-2">
        <input
          autoFocus
          value={search}
          onChange={(e) => {
            onSearchChange(e.target.value);
            onActiveIndexChange(0);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              onActiveIndexChange(
                Math.min(activeIndex + 1, Math.max(filteredValues.length - 1, 0))
              );
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              onActiveIndexChange(Math.max(activeIndex - 1, 0));
            } else if (e.key === "Enter") {
              e.preventDefault();
              const selected = filteredValues[activeIndex];
              if (selected) onCommit(selected);
            } else if (e.key === "Escape") {
              e.preventDefault();
              onCancel();
            }
          }}
          placeholder="Search valid values..."
          className="w-full rounded border border-[var(--color-border-light)] px-2 py-1 text-xs outline-none focus:border-amber-400"
        />
      </div>

      <div className="max-h-48 overflow-auto py-1">
        {filteredValues.length === 0 ? (
          <div className="px-3 py-2 text-xs text-[var(--color-text-tertiary)]">
            No matches found
          </div>
        ) : (
          filteredValues.map((value, idx) => (
            <button
              key={value}
              type="button"
              className={cn(
                "block w-full px-3 py-2 text-left text-xs",
                idx === activeIndex
                  ? "bg-amber-50 text-amber-900"
                  : "bg-white text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)]"
              )}
              onMouseEnter={() => onActiveIndexChange(idx)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onCommit(value)}
            >
              <span className="flex w-full items-center justify-between gap-2">
                <span>{value}</span>
                {isPrioritizedValue(value) && (
                  <span className="shrink-0 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                    <Star className="h-3 w-3 fill-amber-500 stroke-amber-500" />
                    Suggested
                  </span>
                )}
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

// ── Table Component ───────────────────────────────────────────

export function DqxTable({
  headers,
  rows,
  columnMappings,
  validation,
  visibleRowIndices,
  selectedBuckets,
  contractRuleLookup,
  columnAllowedValuesLookup,
  maxHeight = 520,
  className,
  canEdit = true,
  cellEdits,
  onCellEdited,
  onCellReverted,
}: DqxTableProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<{
    rules: string[];
    x: number;
    y: number;
    visible: boolean;
  }>({ rules: [], x: 0, y: 0, visible: false });
  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [dropdownSearch, setDropdownSearch] = useState("");
  const [activeDropdownIndex, setActiveDropdownIndex] = useState(0);
  const { track } = useTelemetry();

  const editsMap = cellEdits ?? EMPTY_EDITS;

  /** `undefined` = show all rows; `[]` = filter matches nothing. */
  const rowIndexByVirtualIndex = useMemo(() => {
    if (visibleRowIndices === undefined) return null;
    return visibleRowIndices;
  }, [visibleRowIndices]);

  const virtualRowCount =
    rowIndexByVirtualIndex === null ? rows.length : rowIndexByVirtualIndex.length;

  const selectedBucketSet = useMemo(() => {
    const arr = selectedBuckets ?? [];
    return new Set<DqxSummaryCheckTypeBucket>(arr);
  }, [selectedBuckets]);

  const shouldHighlightCell = useCallback(
    (cellError: typeof validation.cellErrors extends Map<string, infer T> ? T : never) => {
      if (selectedBucketSet.size === 0) return true;

      // If contract mapping isn't available yet, don't hide existing highlights.
      if (contractRuleLookup === undefined) {
        const hasNames = (cellError.ruleNames ?? []).some((n: string) => n.trim());
        if (!hasNames) return selectedBucketSet.has("other");
        return true;
      }

      const names = (cellError.ruleNames ?? []).map((n: string) => n.trim()).filter(Boolean);
      if (names.length === 0) return selectedBucketSet.has("other");

      for (const nm of names) {
        const meta = contractRuleLookup?.get(normalizeDqxRuleName(nm));
        const bucket = mapContractRuleTypeToBucket(meta?.rule_type, {
          ruleName: nm,
          checkFunction: meta?.check_function,
        });
        if (selectedBucketSet.has(bucket)) return true;
      }
      for (const fn of cellError.ruleFunctions ?? []) {
        const bucket = mapContractRuleTypeToBucket(undefined, { checkFunction: fn });
        if (selectedBucketSet.has(bucket)) return true;
      }
      return false;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedBucketSet, contractRuleLookup]
  );

  const virtualizer = useVirtualizer({
    count: virtualRowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: OVERSCAN,
  });

  // Build rename map for headers
  const renameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of columnMappings) {
      map.set(m.sourceColumn, m.targetColumn);
    }
    return map;
  }, [columnMappings]);

  // Build column index lookup: header name -> index in headers array
  const headerIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    headers.forEach((h, i) => map.set(h, i));
    return map;
  }, [headers]);

  // ── Shared grid template ──────────────────────────────────
  const gridTemplate = `${ROW_NUM_WIDTH}px repeat(${headers.length}, ${COL_WIDTH}px)`;
  const totalWidth = ROW_NUM_WIDTH + headers.length * COL_WIDTH;

  // Tab order: failing cells + pending edits, sorted by row then column.
  const editableKeyOrder = useMemo(() => {
    const keys = new Set<string>();
    for (const [key, err] of validation.cellErrors) {
      if (shouldHighlightCell(err)) keys.add(key);
    }
    for (const key of editsMap.keys()) keys.add(key);
    return Array.from(keys).sort((a, b) => {
      const [ar, ac] = a.split("-").map(Number);
      const [br, bc] = b.split("-").map(Number);
      return ar !== br ? (ar ?? 0) - (br ?? 0) : (ac ?? 0) - (bc ?? 0);
    });
  }, [validation.cellErrors, editsMap, shouldHighlightCell]);

  // Cell hover handler for error tooltips
  const handleCellHover = useCallback(
    (e: React.MouseEvent, rowIndex: number, colIndex: number) => {
      const key = `${rowIndex}-${colIndex}`;
      const cellError = validation.cellErrors.get(key);
      if (cellError) {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setTooltip({
          rules: cellError.rules,
          x: rect.left + rect.width / 2,
          y: rect.top,
          visible: true,
        });
      }
    },
    [validation.cellErrors]
  );

  const handleCellLeave = useCallback(() => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  }, []);

  return (
    <>
      <div
        className={cn(
          "rounded-xl border border-[var(--color-border-light)] overflow-hidden",
          className
        )}
      >
        <div ref={parentRef} className="overflow-auto" style={{ maxHeight }}>
          {/* Fixed-width inner wrapper prevents grid columns from collapsing */}
          <div style={{ width: `${totalWidth}px` }}>
            {/* ── Sticky Dual-Row Header ── */}
            <div
              className="sticky top-0 z-10 grid border-b border-[var(--color-border-light)] bg-[var(--color-bg-secondary)]"
              style={{
                gridTemplateColumns: gridTemplate,
                height: HEADER_HEIGHT,
              }}
            >
              {/* Row number header (empty) */}
              <div className="flex items-center justify-center border-r border-[var(--color-border-light)]" />

              {/* Column headers */}
              {headers.map((header) => {
                const targetName = renameMap.get(header);
                return (
                  <div
                    key={header}
                    className="flex flex-col justify-center px-3 py-1.5 border-r border-[var(--color-border-light)] overflow-hidden"
                    title={targetName ? `${header} -> ${targetName}` : header}
                  >
                    <span className="text-[11px] text-[var(--color-text-tertiary)] italic truncate leading-tight">
                      {header}
                    </span>
                    {targetName && (
                      <span className="text-xs font-semibold text-[var(--color-text-primary)] truncate leading-tight mt-0.5">
                        {targetName}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ── Virtualized Rows ── */}
            <div style={{ height: `${virtualizer.getTotalSize()}px`, position: "relative" }}>
              {virtualizer.getVirtualItems().map((virtualRow) => {
                const originalRowIndex = rowIndexByVirtualIndex
                  ? rowIndexByVirtualIndex[virtualRow.index]!
                  : virtualRow.index;
                const row = rows[originalRowIndex]!;
                const rowIndex = originalRowIndex;

                return (
                  <div
                    key={virtualRow.key}
                    className="grid border-b border-[var(--color-border-light)]"
                    style={{
                      gridTemplateColumns: gridTemplate,
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                      // Lift the editing row so the dropdown popover isn't covered.
                      zIndex: editingCell?.startsWith(`${rowIndex}-`) ? 40 : 1,
                    }}
                  >
                    {/* Row number */}
                    <div className="flex items-center justify-center text-xs text-[var(--color-text-tertiary)] border-r border-[var(--color-border-light)] bg-white">
                      {rowIndex + 1}
                    </div>

                    {/* Data cells */}
                    {headers.map((header) => {
                      const colIndex = headerIndexMap.get(header) ?? -1;
                      const cellKey = `${rowIndex}-${colIndex}`;
                      const cellError = validation.cellErrors.get(cellKey);
                      const highlightError = cellError ? shouldHighlightCell(cellError) : false;
                      const edit = editsMap.get(cellKey);
                      const originalValue = String(row[header] ?? "");
                      const isEditing = editingCell === cellKey;
                      const displayValue = edit?.newValue ?? originalValue;
                      const isEditable = canEdit && (highlightError || edit !== undefined);
                      const allowedValues = columnAllowedValuesLookup?.get(header) ?? [];
                      const hasAllowedValues = allowedValues.length > 0;
                      const editState = edit?.status;

                      const closeEditor = () => {
                        setEditingCell(null);
                        setDropdownSearch("");
                        setActiveDropdownIndex(0);
                      };

                      const handleCellClick = () => {
                        if (!isEditable) return;
                        setEditingCell(cellKey);
                        setDropdownSearch("");
                        setActiveDropdownIndex(0);
                        setTooltip((prev) => ({ ...prev, visible: false }));
                        track("ManualCleanse.CellEditStarted", {
                          cellKey,
                          column: header,
                          hadPendingEdit: edit !== undefined,
                        });
                      };

                      const commitEdit = (rawValue: string, moveToNext: boolean) => {
                        const newVal = rawValue.trim();
                        closeEditor();
                        if (newVal === "" || newVal === originalValue.trim()) {
                          // Empty or unchanged -> revert any pending edit.
                          if (edit !== undefined) {
                            onCellReverted?.(cellKey);
                            track("ManualCleanse.CellEditCancelled", { cellKey });
                          }
                          return;
                        }
                        onCellEdited?.({
                          cellKey,
                          rowIndex,
                          columnIndex: colIndex,
                          columnName: header,
                          oldValue: originalValue,
                          newValue: newVal,
                        });
                        track("ManualCleanse.CellEditCommitted", {
                          cellKey,
                          column: header,
                          newLen: newVal.length,
                          source: hasAllowedValues ? "dropdown" : "freetext",
                        });
                        if (moveToNext) {
                          const idx = editableKeyOrder.indexOf(cellKey);
                          const nextKey =
                            idx >= 0 && idx + 1 < editableKeyOrder.length
                              ? editableKeyOrder[idx + 1]
                              : undefined;
                          if (nextKey) setEditingCell(nextKey);
                        }
                      };

                      const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
                        commitEdit(e.currentTarget.value, false);
                      };

                      const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          commitEdit(e.currentTarget.value, false);
                        } else if (e.key === "Tab") {
                          e.preventDefault();
                          commitEdit(e.currentTarget.value, true);
                        } else if (e.key === "Escape") {
                          e.preventDefault();
                          closeEditor();
                          track("ManualCleanse.CellEditCancelled", { cellKey });
                        }
                      };

                      const handleDropdownCommit = (newVal: string) => {
                        setTooltip((prev) => ({ ...prev, visible: false }));
                        commitEdit(newVal, false);
                      };

                      const handleDropdownCancel = () => {
                        closeEditor();
                        track("ManualCleanse.CellEditCancelled", { cellKey });
                      };

                      return (
                        <div
                          key={header}
                          className={cn(
                            // relative + overflow-visible so the dropdown popover isn't clipped.
                            "relative flex items-center px-3 text-xs border-r border-[var(--color-border-light)] transition-colors overflow-visible",
                            editState === "pending"
                              ? "bg-amber-50 text-amber-900 ring-1 ring-inset ring-amber-400"
                              : editState === "revalidated-pass"
                                ? "bg-emerald-50 text-emerald-900"
                                : editState === "revalidated-fail"
                                  ? "bg-red-100/80 text-red-900"
                                  : highlightError
                                    ? "bg-red-100/80 text-red-900"
                                    : "bg-white text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)]/50",
                            isEditable && "cursor-pointer",
                            isEditing && "ring-2 ring-inset ring-blue-400"
                          )}
                          title={
                            editState === "pending"
                              ? "Pending re-validation — click to edit again"
                              : editState === "revalidated-pass"
                                ? "Corrected and passed re-validation"
                                : editState === "revalidated-fail"
                                  ? "Edit still fails — click to correct"
                                  : highlightError && canEdit
                                    ? "Click to correct this value"
                                    : displayValue
                          }
                          onClick={isEditable ? handleCellClick : undefined}
                          onMouseEnter={
                            (highlightError || editState === "revalidated-fail") && !isEditing
                              ? (e) => handleCellHover(e, rowIndex, colIndex)
                              : undefined
                          }
                          onMouseLeave={handleCellLeave}
                        >
                          {isEditing ? (
                            hasAllowedValues ? (
                              <ConstrainedDropdownEditor
                                originalValue={originalValue}
                                allowedValues={allowedValues}
                                prioritizedValues={[]}
                                search={dropdownSearch}
                                activeIndex={activeDropdownIndex}
                                onSearchChange={setDropdownSearch}
                                onActiveIndexChange={setActiveDropdownIndex}
                                onCommit={handleDropdownCommit}
                                onCancel={handleDropdownCancel}
                              />
                            ) : (
                              <input
                                autoFocus
                                defaultValue={displayValue}
                                className="w-full bg-transparent outline-none border-b border-blue-500 text-xs"
                                onBlur={handleInputBlur}
                                onKeyDown={handleInputKeyDown}
                                onClick={(e) => e.stopPropagation()}
                                onFocus={(e) => e.currentTarget.select()}
                              />
                            )
                          ) : (
                            <span className="truncate flex items-center gap-1">
                              {displayValue}
                              {editState === "pending" && (
                                <Pencil
                                  className="w-3 h-3 shrink-0 text-amber-600"
                                  aria-label="Pending re-validation"
                                />
                              )}
                              {editState === "revalidated-pass" && (
                                <span
                                  className="shrink-0 text-emerald-600 font-semibold"
                                  aria-label="Revalidated"
                                >
                                  ✓
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Tooltip portal (rendered outside table scroll container) */}
      <ErrorTooltip rules={tooltip.rules} visible={tooltip.visible} x={tooltip.x} y={tooltip.y} />
    </>
  );
}
